/**
 * @file    questionCalculationMapping.ts
 * @scope   SHARED
 * @summary Pure parsers that turn the Guardrails chart config into the option shapes consumed by
 *          the question and calculation pickers.
 * @consumes Guardrails chart config payload
 * @produces mapQuestionsToOptions, mapCalculationsToOptions, groupCalculationsByQuestion
 * @sideEffects None. All functions are pure; the caller owns the state updates.
 * @notes LEGACY-ONLY in practice: Enterprise V2 widgets configure fields instead of
 *        questions/calculations, so these parsers never run for them.
 */
import { SelectOptions } from 'core-ui/MultipleSelectSortable/MultipleSelectSortable';
import { sortByString, SortOptions } from 'helpers/sorting';
import { strip } from 'helpers/utilities';

/**
 * Maps a list of Guardrails questions to picker options.
 *
 * WHY: `strip` removes the HTML markup survey authors embed in question texts, which would
 * otherwise leak into the dropdown labels.
 *
 * @param questions Raw questions array (`Config.questions` or `customWidgetConfig.questions`).
 * @param sorted    When `true` the result is sorted alphabetically by label.
 */
export const mapQuestionsToOptions = (questions: any[] = [], sorted = false): SelectOptions[] => {
  const options: SelectOptions[] = questions.map((question) => ({
    value: question?.questionLabel,
    label: strip(question?.shortText),
    longText: strip(question?.longText),
    raw: question,
  }));

  if (sorted) sortByString(options, 'label', SortOptions.ASC);
  return options;
};

/**
 * Maps persisted calculations to picker options, preserving every original property so the
 * selection can be written back to the widget config untouched.
 *
 * @param calculations Raw calculations array (`customWidgetConfig.calculations`).
 */
export const mapCalculationsToOptions = (calculations: any[] = []): any[] =>
  calculations.map((calculation) => ({
    ...calculation,
    value: calculation.id,
    label: strip(calculation.displayName),
  }));

/**
 * Groups the available calculations under the question they belong to, producing the grouped
 * option list that `GroupSelectSortable` renders.
 *
 * Questions without calculations are dropped, and the question order drives the group order.
 *
 * @param chartData Guardrails chart config containing `Questions` and `Calculations`.
 * @returns Grouped options, or an empty array when the chart exposes no calculations.
 */
export const groupCalculationsByQuestion = (chartData: any): any[] => {
  if (!chartData?.calculations) return [];

  const questions: any[] = chartData?.questions ?? [];
  const calculations: any[] = chartData.calculations;

  // Index the calculations by the question they measure.
  const calculationsByQuestion = calculations.reduce(
    (acc: { [key: string]: any[] }, calculation: { [key: string]: any }) => {
      const key = calculation['questionName'];
      const currentGroup = acc[key] ?? [];
      return {
        ...acc,
        [key]: [
          ...currentGroup,
          { ...calculation, value: calculation.id, label: strip(calculation.displayName) },
        ],
      };
    },
    {}
  );

  // Walk the questions so the groups follow the order Guardrails returned.
  return questions.reduce((acc: any[], question: any) => {
    const key = question['questionLabel'];
    if (calculationsByQuestion[key]) {
      acc.push({
        label: question['shortText'],
        longText: question['longText'],
        options: calculationsByQuestion[key],
      });
    }
    return acc;
  }, []);
};
